/**
 * Dimension Rulers for Yard Map Canvas.
 *
 * Renders ruler tick marks along the top and left edges of the SVG canvas,
 * a yard dimensions label, and the current zoom scale percentage.
 */

import { niceTickInterval } from "./shapeUtils";

interface DimensionRulersProps {
  yardWidth: number;
  yardHeight: number;
  scale: number;
  /** Unit label shown in the dimensions readout (e.g. "ft"). */
  unit?: string;
}

const RULER_SIZE = 20; // pixels for ruler gutter

function DimensionRulers({
  yardWidth,
  yardHeight,
  scale,
  unit = "ft",
}: DimensionRulersProps) {
  const fontSize = 8 / scale;
  const majorTickLen = 8 / scale;
  const minorTickLen = 4 / scale;
  const rulerGutter = RULER_SIZE / scale;

  // Adaptive tick spacing so small (30 ft) and large (3000 ft) maps both read
  // cleanly; the minor tick sits at half the major interval.
  const majorInterval = niceTickInterval(Math.max(yardWidth, yardHeight));
  const minorInterval = majorInterval / 2;

  const majorTicksX: number[] = [];
  for (let v = 0; v <= yardWidth; v += majorInterval) majorTicksX.push(v);

  const minorTicksX: number[] = [];
  for (let v = minorInterval; v < yardWidth; v += minorInterval) {
    if (v % majorInterval !== 0) minorTicksX.push(v);
  }

  const majorTicksY: number[] = [];
  for (let v = 0; v <= yardHeight; v += majorInterval) majorTicksY.push(v);

  const minorTicksY: number[] = [];
  for (let v = minorInterval; v < yardHeight; v += minorInterval) {
    if (v % majorInterval !== 0) minorTicksY.push(v);
  }

  const scalePercent = Math.round(scale * 100);

  return (
    <g style={{ pointerEvents: "none" }}>
      {/* Top ruler background */}
      <rect
        x={0}
        y={-rulerGutter}
        width={yardWidth}
        height={rulerGutter}
        fill="var(--color-surface-secondary, #f8fafc)"
        fillOpacity={0.9}
      />

      {/* Top ruler ticks */}
      {majorTicksX.map((v) => (
        <g key={`tx-${v}`}>
          <line
            x1={v}
            y1={-majorTickLen}
            x2={v}
            y2={0}
            stroke="var(--color-border, #cbd5e1)"
            strokeWidth={1 / scale}
          />
          <text
            x={v + 2 / scale}
            y={-majorTickLen + fontSize}
            fontSize={fontSize}
            fill="var(--text-tertiary, #94a3b8)"
          >
            {v}
          </text>
        </g>
      ))}
      {minorTicksX.map((v) => (
        <line
          key={`tmx-${v}`}
          x1={v}
          y1={-minorTickLen}
          x2={v}
          y2={0}
          stroke="var(--color-border, #cbd5e1)"
          strokeWidth={0.5 / scale}
        />
      ))}

      {/* Left ruler background */}
      <rect
        x={-rulerGutter}
        y={0}
        width={rulerGutter}
        height={yardHeight}
        fill="var(--color-surface-secondary, #f8fafc)"
        fillOpacity={0.9}
      />

      {/* Left ruler ticks */}
      {majorTicksY.map((v) => (
        <g key={`ty-${v}`}>
          <line
            x1={-majorTickLen}
            y1={v}
            x2={0}
            y2={v}
            stroke="var(--color-border, #cbd5e1)"
            strokeWidth={1 / scale}
          />
          <text
            x={-rulerGutter + 2 / scale}
            y={v + fontSize / 2}
            fontSize={fontSize}
            fill="var(--text-tertiary, #94a3b8)"
          >
            {v}
          </text>
        </g>
      ))}
      {minorTicksY.map((v) => (
        <line
          key={`tmy-${v}`}
          x1={-minorTickLen}
          y1={v}
          x2={0}
          y2={v}
          stroke="var(--color-border, #cbd5e1)"
          strokeWidth={0.5 / scale}
        />
      ))}

      {/* Dimensions label (top-right) */}
      <text
        x={yardWidth - 4 / scale}
        y={-4 / scale}
        fontSize={fontSize * 1.1}
        fill="var(--text-tertiary, #94a3b8)"
        textAnchor="end"
        fontWeight="500"
      >
        {yardWidth} × {yardHeight} {unit} · {scalePercent}%
      </text>

      {/* Corner square */}
      <rect
        x={-rulerGutter}
        y={-rulerGutter}
        width={rulerGutter}
        height={rulerGutter}
        fill="var(--color-surface-secondary, #f8fafc)"
        fillOpacity={0.9}
      />
    </g>
  );
}

export default DimensionRulers;
